import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      // Pull token from localStorage to support cross-site mobile clients where
      // cookies get partitioned/dropped. Sent alongside withCredentials so
      // either transport keeps the user signed in.
      const tok = (() => { try { return localStorage.getItem('access_token'); } catch { return null; } })();
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true,
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, force = false) => {
    const response = await axios.post(`${API_URL}/api/auth/login`,
      { email, password, force },
      { withCredentials: true }
    );
    // Single-device: the account is already signed in elsewhere. No session was
    // started — let the caller confirm takeover and re-call with force=true.
    if (response.data?.already_logged_in) {
      return response.data;
    }
    // Persist both tokens so mobile (where cookies may be dropped on cross-site
    // requests) can still authenticate via Authorization: Bearer headers.
    try {
      if (response.data?.access_token)  localStorage.setItem('access_token',  response.data.access_token);
      if (response.data?.refresh_token) localStorage.setItem('refresh_token', response.data.refresh_token);
    } catch (_) {}
    // After login the cookie/header sets are fresh — refetch /auth/me to grab
    // the full user shape (username, linked_app_token) the login endpoint
    // doesn't include.
    await checkAuth();
    return response.data;
  };

  const register = async (email, password, name, role = 'user') => {
    const response = await axios.post(`${API_URL}/api/auth/register`,
      { email, password, name, role },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try { await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true }); } catch {}
    // clear session-only quick-filter values so they reset on next login
    Object.keys(sessionStorage).filter(k => k.startsWith('qfv_')).forEach(k => sessionStorage.removeItem(k));
    try { localStorage.removeItem('access_token'); localStorage.removeItem('refresh_token'); } catch (_) {}
    setUser(null);
  };

  const refreshToken = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
      await checkAuth();
    } catch (error) {
      setUser(null);
    }
  };

  const isCreator = user?.role === 'creator';
  const isAdmin = user?.role === 'admin' || user?.role === 'creator';

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      logout,
      refreshToken,
      checkAuth,
      isCreator,
      isAdmin,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
